import React, { useEffect, useMemo, useState } from 'react';
import {
  AGE_PRESETS,
  biologicalAgeDays,
  formatAge,
  labelTargetAge,
  recommendedTarget,
  targetAgeOptions
} from '../../utils/flockAge';

/**
 * Arrival date + age on that date + optional target market age.
 * Age on arrival is the single source of truth; acquisition age is not a
 * separate question because it was being filled independently and then
 * ignored by the activity schedule.
 */
const FlockTimingFields = ({
  startDate,
  initialAgeDays,
  targetMarketAgeDays,
  type,
  subtype,
  onChange,
  idPrefix = '',
  required = false,
  showTarget = true
}) => {
  const ageNum = initialAgeDays === '' || initialAgeDays === null || initialAgeDays === undefined
    ? ''
    : Number(initialAgeDays);
  const presetMatch = AGE_PRESETS.some((p) => p.value === ageNum);
  const [useCustomAge, setUseCustomAge] = useState(ageNum !== '' && !presetMatch);
  const [useCustomTarget, setUseCustomTarget] = useState(() => {
    if (targetMarketAgeDays === '' || targetMarketAgeDays == null) return false;
    return !targetAgeOptions(type, subtype, Number(ageNum) || 0)
      .includes(Number(targetMarketAgeDays));
  });

  const ageToday = useMemo(
    () => (startDate === '' || ageNum === '' ? null : biologicalAgeDays(startDate, ageNum)),
    [startDate, ageNum]
  );

  const targets = targetAgeOptions(type, subtype, Number(ageNum) || 0);

  useEffect(() => {
    if (!showTarget || useCustomTarget) return;
    const options = targetAgeOptions(type, subtype, Number(ageNum) || 0);
    const current = targetMarketAgeDays === '' || targetMarketAgeDays == null
      ? null
      : Number(targetMarketAgeDays);
    if (current != null && options.includes(current)) return;
    const next = options[0] ?? '';
    if (next !== current && next !== '') onChange({ targetMarketAgeDays: next });
  }, [type, subtype]);

  const setAge = (next, custom) => {
    setUseCustomAge(custom);
    const patch = { initialAgeDays: next };
    if (showTarget && !useCustomTarget) {
      patch.targetMarketAgeDays = recommendedTarget(type, subtype, Number(next) || 0);
    }
    onChange(patch);
  };

  return (
    <>
      <div>
        <label htmlFor={`${idPrefix}startDate`} className="label text-sm sm:text-base">
          Date these birds arrived{required ? ' *' : ''}
        </label>
        <input
          type="date"
          id={`${idPrefix}startDate`}
          required={required}
          className="input text-sm sm:text-base"
          value={startDate || ''}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => onChange({ startDate: e.target.value })}
        />
        <p className="mt-1 text-xs sm:text-sm text-gray-500">
          The day they hatched on this farm, or the day you received them. Not today, unless they arrived today.
        </p>
      </div>

      <div>
        <label htmlFor={`${idPrefix}initialAge`} className="label text-sm sm:text-base">
          How old were they that day?{required ? ' *' : ''}
        </label>
        <select
          id={`${idPrefix}initialAge`}
          className="input text-sm sm:text-base"
          value={useCustomAge ? 'custom' : (ageNum === '' ? '' : String(ageNum))}
          onChange={(e) => {
            const val = e.target.value;
            if (val === 'custom') setAge(ageNum === '' ? '' : ageNum, true);
            else if (val === '') setAge('', false);
            else setAge(Number(val), false);
          }}
        >
          <option value="">Select age on arrival</option>
          {AGE_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
          <option value="custom">Other age (enter days)</option>
        </select>
        {useCustomAge && (
          <input
            type="number"
            min="0"
            max="1095"
            className="input text-sm sm:text-base mt-2"
            placeholder="Age in days on the arrival date"
            value={ageNum === '' ? '' : ageNum}
            onChange={(e) => setAge(e.target.value === '' ? '' : Number(e.target.value), true)}
          />
        )}
        {ageToday !== null && ageNum !== '' && (
          <p className="mt-1 text-xs sm:text-sm text-primary-700">
            That makes them <span className="font-medium">{formatAge(ageToday)}</span> old today. Vaccinations and feed stages will follow this age, not the calendar date you opened the form.
          </p>
        )}
      </div>

      {showTarget && (
        <div>
          <label htmlFor={`${idPrefix}targetAge`} className="label text-sm sm:text-base">
            Target market age (optional)
          </label>
          <select
            id={`${idPrefix}targetAge`}
            className="input text-sm sm:text-base"
            value={useCustomTarget ? 'custom' : (targetMarketAgeDays === '' || targetMarketAgeDays == null ? '' : String(targetMarketAgeDays))}
            onChange={(e) => {
              const val = e.target.value;
              if (val === 'custom') {
                setUseCustomTarget(true);
                onChange({ targetMarketAgeDays: '' });
              } else {
                setUseCustomTarget(false);
                onChange({ targetMarketAgeDays: val === '' ? '' : Number(val) });
              }
            }}
          >
            <option value="">Select recommended</option>
            {targets.map((v) => (
              <option key={v} value={v}>{labelTargetAge(v)}</option>
            ))}
            <option value="custom">Custom…</option>
          </select>
          {useCustomTarget && (
            <input
              type="number"
              min="0"
              className="input text-sm sm:text-base mt-2"
              placeholder="Enter custom days"
              value={targetMarketAgeDays}
              onChange={(e) => onChange({
                targetMarketAgeDays: e.target.value === '' ? '' : Number(e.target.value)
              })}
            />
          )}
          <p className="mt-1 text-xs sm:text-sm text-gray-500">
            Biological age at harvest or peak lay, not days from today.
          </p>
        </div>
      )}
    </>
  );
};

export default FlockTimingFields;
